/**
 * notificationApiClient.ts
 *
 * Thin HTTP transport for the BizFirst Communication API's notification surface.
 * Responsibilities, and only these (SRP): build request bodies, POST them, unwrap
 * the `{ success, errors, data, pageInfo }` envelope, and throw a typed error on
 * failure. No React, no polling, no persisted state — see hooks/useNotifications.ts
 * for the polling/state layer built on top of this.
 *
 * Base route confirmed from
 * BizFirstPayrollV3/src/mvc-server/Go/Communication/BizFirstFi.Go.Communication.Api.Base/Controllers/BaseNotificationController.cs:
 *   [Route("api/v1/communication/[controller]")] on BaseNotificationController
 *   => "api/v1/communication/notification" (controller name minus "Controller").
 *
 * Endpoints implemented here (both POST, both [AuthorizeRegularUserAttribute] —
 * i.e. any request needs a bearer token for a regular authenticated user):
 *   - POST api/v1/communication/notification/search/by-user  (GetByUserSearchRequest)
 *   - POST api/v1/communication/notification/search/unread   (GetUnreadNotificationsSearchRequest)
 *   - POST api/v1/communication/notification/get              (GetByIdWebRequest)
 *
 * Deliberately NOT implemented — out of scope for a read-only header-bell client:
 *   - The other search/* variants (by-status, by-type, by-priority, by-delivery-method,
 *     by-date-range, failed, pending, expired) — same pattern, not needed by the bell.
 *   - MarkReadAsync / MarkSentAsync / ActivateAsync / DeactivateAsync / RetryAsync /
 *     ClearExpiredAsync: `INotificationService` declares these, but
 *     BaseNotificationController.cs has NO [Http...] route for any of them — they are
 *     not reachable over HTTP today. There is intentionally no `markAsRead` method on
 *     this client. Do not add one without first adding the corresponding controller
 *     route on the backend; a client method with nowhere to send its request would be
 *     a fake success.
 *   - create/update/delete/search (GetAll): admin-only or out of scope for a bell widget.
 */

import type {
  GetNotificationByIdRequest,
  GetNotificationsByUserRequest,
  GetUnreadNotificationsRequest,
  IDInfo,
  NotificationApiErrorEntry,
  NotificationGetByIdResponseEnvelope,
  NotificationItem,
  NotificationPageInfoRequest,
  NotificationSearchResponseEnvelope,
  NotificationSearchResult,
} from '../types'

const NOTIFICATION_BASE_PATH = 'api/v1/communication/notification'

/**
 * Thrown for both transport failures (network error, non-2xx with no recognizable
 * envelope) and application-level failures (`success: false` in an otherwise
 * well-formed 200 response — the search endpoints always return HTTP 200 even on
 * failure, per `BaseNotificationController.WebResponse_Search` always calling
 * `Ok(response)`, so `success` must be checked independently of HTTP status).
 */
export class NotificationApiError extends Error {
  /** HTTP status code, when the failure is associated with one response. */
  readonly statusCode?: number
  /** First error's `code`, if the backend supplied one. */
  readonly code?: string
  /** Full `errors` array from the response envelope, when available. */
  readonly errors: NotificationApiErrorEntry[]

  constructor(
    message: string,
    options: { statusCode?: number; code?: string; errors?: NotificationApiErrorEntry[] } = {}
  ) {
    super(message)
    this.name = 'NotificationApiError'
    this.statusCode = options.statusCode
    this.code = options.code
    this.errors = options.errors ?? []
  }
}

export interface NotificationApiClientOptions {
  /** Base URL of the Communication API, e.g. "https://localhost:10006". No trailing
   *  slash required — it is normalized. This package stays configuration-agnostic
   *  and never hardcodes or defaults this; the consuming app supplies it. */
  apiBaseUrl: string
  /** Returns the current bearer token, or null/undefined if the caller has none
   *  (e.g. not logged in yet). Invoked fresh on every request so a refreshed token
   *  is always picked up — this package does not cache or own the token itself,
   *  by design (SRP: token acquisition/refresh belongs to the auth package, not
   *  here). Mirrors the constructor-injection pattern already used by
   *  @passport/api-client's HttpClient. */
  getAuthToken: () => string | null | undefined
  /** Per-request timeout in milliseconds. Defaults to 15000. */
  timeoutMs?: number
  /** Override for `fetch`, primarily for testing. Defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch
}

/** Narrow, unwrap-only response reader shared by every method below. */
async function postAndUnwrap<TRequestBody, TEnvelope extends { success: boolean; errors: NotificationApiErrorEntry[] }>(
  options: Required<Pick<NotificationApiClientOptions, 'apiBaseUrl' | 'timeoutMs' | 'fetchImpl'>> &
    Pick<NotificationApiClientOptions, 'getAuthToken'>,
  path: string,
  body: TRequestBody
): Promise<TEnvelope> {
  const url = `${options.apiBaseUrl.replace(/\/+$/, '')}/${path}`
  const token = options.getAuthToken?.()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let response: Response
  try {
    response = await options.fetchImpl(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(options.timeoutMs),
    })
  } catch (networkError) {
    throw new NotificationApiError(
      networkError instanceof Error ? networkError.message : 'Network request failed',
      {}
    )
  }

  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json') ?? false
  const parsedBody: unknown = isJson ? await response.json().catch(() => undefined) : undefined

  // Check the {success, errors} envelope before trusting anything else in the body
  // (per the established frontend-side convention, see
  // src/playground/apps/playground-app/src/core/components/ActionRunner.tsx's
  // extractApiErrors, and @passport/api-client's HttpClient.handleResponse).
  if (isRecord(parsedBody) && 'success' in parsedBody) {
    const envelope = parsedBody as TEnvelope
    if (envelope.success === false) {
      const errors = Array.isArray(envelope.errors) ? envelope.errors : []
      const first = errors.find((entry) => isRecord(entry) && typeof entry.message === 'string')
      throw new NotificationApiError(first?.message ?? response.statusText ?? 'Notification API request failed', {
        statusCode: response.status,
        code: first?.code,
        errors,
      })
    }
    return envelope
  }

  // No recognizable envelope: fall back to plain HTTP status. Covers cases like an
  // auth gateway 401 that returns a non-GoWebResponse body, or a 5xx with an HTML
  // error page.
  if (!response.ok) {
    throw new NotificationApiError(response.statusText || `Request failed with status ${response.status}`, {
      statusCode: response.status,
    })
  }

  throw new NotificationApiError('Unexpected response shape from notification API (missing "success" field).', {
    statusCode: response.status,
  })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Notification API client. Construct one per `apiBaseUrl`/token-source pair
 * (typically once per app, alongside whatever else wires up API base URLs) and
 * reuse it — it holds no mutable state itself, just configuration.
 */
export class NotificationApiClient {
  private readonly apiBaseUrl: string
  private readonly getAuthToken: () => string | null | undefined
  private readonly timeoutMs: number
  private readonly fetchImpl: typeof fetch

  constructor(options: NotificationApiClientOptions) {
    if (!options.apiBaseUrl) {
      throw new Error('NotificationApiClient requires a non-empty apiBaseUrl.')
    }
    this.apiBaseUrl = options.apiBaseUrl
    this.getAuthToken = options.getAuthToken
    this.timeoutMs = options.timeoutMs ?? 15_000
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis)
  }

  private get transportOptions() {
    return {
      apiBaseUrl: this.apiBaseUrl,
      getAuthToken: this.getAuthToken,
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
    }
  }

  /**
   * POST api/v1/communication/notification/search/by-user
   * Notifications for a specific user (read and unread both included — the caller
   * decides what to do with `readAt`/`isNotificationUnread()`).
   */
  async searchByUser(
    userID: IDInfo,
    params: { pageInfo?: NotificationPageInfoRequest; filters?: Record<string, unknown> } = {}
  ): Promise<NotificationSearchResult> {
    const body: GetNotificationsByUserRequest = {
      userID,
      pageInfo: params.pageInfo,
      filters: params.filters,
    }
    const envelope = await postAndUnwrap<GetNotificationsByUserRequest, NotificationSearchResponseEnvelope>(
      this.transportOptions,
      `${NOTIFICATION_BASE_PATH}/search/by-user`,
      body
    )
    return { items: envelope.data ?? [], pageInfo: envelope.pageInfo }
  }

  /**
   * POST api/v1/communication/notification/search/unread
   * Unread notifications for the current authenticated user (inferred server-side
   * from the bearer token — the request body carries no user identifier).
   * `pageInfo.totalRecords` on the result is the authoritative unread count,
   * independent of `pageSize` (see NotificationPageInfo's doc comment in types.ts).
   */
  async searchUnread(
    params: { pageInfo?: NotificationPageInfoRequest; filters?: Record<string, unknown> } = {}
  ): Promise<NotificationSearchResult> {
    const body: GetUnreadNotificationsRequest = {
      pageInfo: params.pageInfo,
      filters: params.filters,
    }
    const envelope = await postAndUnwrap<GetUnreadNotificationsRequest, NotificationSearchResponseEnvelope>(
      this.transportOptions,
      `${NOTIFICATION_BASE_PATH}/search/unread`,
      body
    )
    return { items: envelope.data ?? [], pageInfo: envelope.pageInfo }
  }

  /**
   * POST api/v1/communication/notification/get
   * Fetches a single notification by ID. Returns null when not found and
   * `raiseErrorIfNotFound` is left false (the default) — matching
   * `GetByIdWebRequest.RaiseErrorIfNotFound`'s own default. When true, a 404 with a
   * well-formed envelope is treated as a NotificationApiError instead.
   */
  async getById(id: IDInfo, raiseErrorIfNotFound = false): Promise<NotificationItem | null> {
    const body: GetNotificationByIdRequest = { id, raiseErrorIfNotFound }
    const envelope = await postAndUnwrap<GetNotificationByIdRequest, NotificationGetByIdResponseEnvelope>(
      this.transportOptions,
      `${NOTIFICATION_BASE_PATH}/get`,
      body
    )
    return envelope.data ?? null
  }
}
